import React, { useState, useMemo, useContext, useEffect } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, Typography, TextField, InputAdornment, IconButton, Box, Autocomplete, Avatar, Chip, Select, MenuItem, FormControlLabel, Checkbox, Stack, useMediaQuery, useTheme, Tooltip } from '@mui/material';
import { Close as CloseIcon, Share as ShareIcon, Search as SearchIcon, Link } from '@mui/icons-material';
import { ActionButton, BarIconButton } from '../../../buttons/ButtonStyles';
import { firebaseHelper, useTeams, usePermissions, useDesignFirm } from 'ui';
import { useMattoState, usePhysicalObjects, useProjectData } from '../../../MattoState';
import { TeamsInterface, ResourceType, AccessType } from 'ts-interfaces';
import { getUrlFromRef } from '../../../libs/firebase';
import { AuthContext } from '../../../auth/Auth';
import ShareRoundedIcon from '@mui/icons-material/ShareRounded';
import { capitalize } from '../../../libs/util';
import { TEAMS_INVITATION_LINK } from '../../../libs/firebase';

const getAvatarColor = (email: string): string => {
  const colors = ['#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3', '#03a9f4', '#00bcd4', '#009688', '#4caf50', '#8bc34a', '#cddc39', '#ffeb3b', '#ffc107', '#ff9800', '#ff5722'];
  let hash = 0;
  for (let i = 0; i < email.length; i++) hash = email.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

interface AccessSelectProps {
  value: AccessType | 'none';
  onChange: (event: any) => void;
  size?: "small" | "medium";
  disabled?: boolean;
}

const AccessSelect = ({value, onChange, size = "medium", disabled = false}: AccessSelectProps) => (
  <Select {...{value, onChange, size}} variant="outlined" sx={{minWidth: 125}} disabled={disabled}>
    <MenuItem value={AccessType.Write}>Can Edit</MenuItem>
    <MenuItem value={AccessType.Read}>Can View</MenuItem>
    {size === "small" && <MenuItem value="none">No Access</MenuItem>}
  </Select>
);

interface ShareResourceModalProps {
  resourceID: string;
  type: ResourceType;
  title?: string;
  thumbnail?: string;
  disabled?: boolean;
  owner?: string;
}

export const ShareResourceModal = ({resourceID, type, title, thumbnail, disabled = false, owner}: ShareResourceModalProps) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  if (isSmallScreen) return null;
  
  const handleClick = () => {
    if (!disabled) {
      setOpen(true);
    }
  };
  
  return (
    <>
      <IconButton 
        aria-label='share' 
        size="small" 
        onClick={handleClick}
        disabled={disabled}
        sx={{ 
          color: disabled ? '#ccc' : 'inherit',
          '&.Mui-disabled': {
            color: '#ccc'
          }
        }}
      >
        <ShareIcon fontSize="small" />
      </IconButton>
      {open && <ShareResourceModalDialog {...{resourceID, type, title, thumbnail, owner}} onClose={() => setOpen(false)} />}
    </>
  );
}

export const ShareResourceModalCreator = () => {
  const [open, setOpen] = useState(false);
  const saveProjectNow = useMattoState((state) => state.saveProjectNow);
  const setSelectedPhysicalObjectKey = usePhysicalObjects( (state) => state.setSelectedPhysicalObjectKey)
  
  const handleClick = () => {
    const projectData = useProjectData.getState();
		if(!projectData?.projectId) { alert('Please save your project before sharing') }
		else { setSelectedPhysicalObjectKey(null); setOpen(true) }
	}
  return (
    <>
      <Tooltip title="Share" arrow>
          <BarIconButton aria-label='Share' edge='start' size="large" onClick={handleClick}  
            disabled={saveProjectNow ? true : false} >
            <ShareRoundedIcon style={saveProjectNow===true ? {color:'grey'}: {color:'black'}} />
          </BarIconButton>	
        </Tooltip>    
        {open && <ShareResourceCreatorDialog onClose={() => setOpen(false)} />}
    </>
  )
}
const ShareResourceCreatorDialog = ({onClose}: {onClose: () => void}) => {
  const projectData:any = useProjectData.getState();
  if (!projectData.projectId) return null;
  return <ShareResourceModalDialog {...{resourceID: projectData.projectId, type: ResourceType.Boards, title: projectData.title, thumbnail: projectData.screenshot, owner: projectData.userId}} onClose={onClose} />
}



const ShareResourceModalDialog = ({resourceID, type, title, thumbnail, owner, onClose}: ShareResourceModalProps & {onClose: () => void}) => {
  const {currentUser} = useContext(AuthContext);
  const permissions = usePermissions(currentUser?.uid, firebaseHelper.db);
  const designFirmID = permissions?.permissions?.teamsDesigner?.[0];
  const { firm } = useDesignFirm(firebaseHelper.db, designFirmID, null);
  const { teams, updateTeamMember } = useTeams(designFirmID, firebaseHelper.db);
  const [updatingMembers, setUpdatingMembers] = useState<Set<string>>(new Set());
  const [selectedMembers, setSelectedMembers] = useState<TeamsInterface[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [accessLevel, setAccessLevel] = useState<AccessType>(AccessType.Write);
  const [preventUserEdit, setPreventUserEdit] = useState(true);
  const [sendingInvitation, setSendingInvitation] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  
  // const showInviteSection = inputValue.trim().length > 0 || selectedMembers.length > 0;
  const resourceName = type.toLowerCase().slice(0, -1);
  const resourceKeys = useMemo(() => ({
    [ResourceType.Collections]: { read: 'collectionRead', write: 'collectionWrite' },
    [ResourceType.Projects]: { read: 'projectRead', write: 'projectWrite' },
    [ResourceType.Boards]: { read: 'boardRead', write: 'boardWrite' }
  }[type]), [type]);

  const { ownerMember, teamMemberWithAccess, teamMemberWithoutAccess } = useMemo(() => {
    const withAccess: TeamsInterface[] = [];
    const withoutAccess: TeamsInterface[] = [];
    let ownerMember: TeamsInterface | null = null;

    teams?.forEach(member => {
      if (!member?.uid) return;

      // Find the owner
      if (owner && member.uid === owner) {
        ownerMember = member;
        return;
      }

      if (member.uid === currentUser?.uid) return; // Skip current user if not owner

      const hasAccess = member.permissions && (
        member.permissions[resourceKeys.read]?.includes(resourceID) ||
        member.permissions[resourceKeys.write]?.includes(resourceID)
      );
      (hasAccess ? withAccess : withoutAccess).push(member);
    });
    
    return { ownerMember, teamMemberWithAccess: withAccess, teamMemberWithoutAccess: withoutAccess };
  }, [currentUser?.uid, teams, resourceID, resourceKeys, owner]);

  // Ensure selectedMembers only contains valid options from teamMemberWithoutAccess
  const validSelectedMembers = useMemo(() => {
    return selectedMembers.filter(selected => 
      teamMemberWithoutAccess.some(option => option.uid === selected.uid)
    );
  }, [selectedMembers, teamMemberWithoutAccess]);

  const showInviteSection = inputValue.trim().length > 0 || validSelectedMembers.length > 0;

  const getCurrentAccessLevel = (member: TeamsInterface): AccessType | 'none' => {
    if (!member.permissions) return 'none';
    if (member.permissions[resourceKeys.write]?.includes(resourceID)) return AccessType.Write;
    if (member.permissions[resourceKeys.read]?.includes(resourceID)) return AccessType.Read;
    return 'none';
  };

  const sendEmails = async (emails: string[], inviterName: string, inviterEmail: string) => {
    try {
      const response = await fetch(TEAMS_INVITATION_LINK, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          emails,
          name: title,
          accessType: accessLevel,
          type: type,
          customMessage,
          inviterName,
          inviterEmail
        })
      });

      // Check if the response is successful
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Check if the API returned an error in the response body
      if (data.error) {
        throw new Error(data.error);
      }
      return data;

    } catch (error) {
      console.error('Error sending invitations:', error);
      throw error; // Re-throw the error to be handled by the caller
    }
  }

  const handleSendInvitations = async () => {
    try {
      useMattoState.getState().setSnackbarMessage('Sending invitations...');
      setSendingInvitation(true);
      await Promise.all(validSelectedMembers.map(m => m.uid && updateTeamMember(m.uid, resourceID, type, accessLevel)));
      const count = validSelectedMembers.length;
      const emails = validSelectedMembers.map(m => m.email).filter(email => email);
      const inviterName = teams && teams.find(team => team.uid === currentUser?.uid)?.first_name || '';
      const inviterEmail = teams && teams.find(team => team.uid === currentUser?.uid)?.email || '';

      await sendEmails(emails, inviterName, inviterEmail);
      
      useMattoState.getState().setSnackbarMessage(
        `Successfully invited ${count} team member${count === 1 ? '' : 's'}`
      );
      setSelectedMembers([]);
      setInputValue('');
      setCustomMessage('');
    } catch (error) {
      console.error('Error in handleSendInvitations:', error);
      useMattoState.getState().setSnackbarMessage('Failed to send invitations. Please try again.');
    } finally {
      setSendingInvitation(false);
    }
  };

  const handleAccessChange = async (member: TeamsInterface, level: AccessType | 'none') => {
    if (!member.uid) return;

    const memberUid = member.uid;
    setUpdatingMembers(prev => new Set(prev).add(memberUid));

    try {
      const isDeleting = level === 'none';
      const name = `${member.profile?.first_name || ''} ${member.profile?.last_name || ''}`.trim() || member.email;
      useMattoState.getState().setSnackbarMessage(
        isDeleting ? `Removing ${name}'s access` : `Updating ${name}'s access to ${level === AccessType.Write ? 'edit' : 'view'}`
      );
      await updateTeamMember(member.uid, resourceID, type, isDeleting ? AccessType.Read : level, isDeleting);
      useMattoState.getState().setSnackbarMessage(
        isDeleting ? `Removed ${name}'s access` : `Updated ${name}'s access to ${level === AccessType.Write ? 'edit' : 'view'}`
      );
    } catch (error) {
      useMattoState.getState().setSnackbarMessage('Failed to update access. Please try again.');
    } finally {
      setUpdatingMembers(prev => {
        const newSet = new Set(prev);
        newSet.delete(memberUid);
        return newSet;
      });
    }
  };

  const handleCopyLink = async () => {
    try {
      const baseUrl = window.location.origin;
      const resourcePath = type === ResourceType.Boards ? 'show' : 
                          type === ResourceType.Projects ? 'project' : 'collection';
      let shareUrl = `${baseUrl}/${resourcePath}/${resourceID}${type === ResourceType.Boards && preventUserEdit ? '?ee=true' : ''}`; 
      if (ResourceType.Projects === type && resourceID) {
          shareUrl = `${baseUrl}/projects/show/${resourceID}`;
      }
      else if (ResourceType.Collections === type && resourceID) {
          shareUrl = `${baseUrl}/project/new?collectionId=${resourceID}&collectionType=my`;
      }
      await navigator.clipboard.writeText(shareUrl);
      useMattoState.getState().setSnackbarMessage('Link copied to clipboard');
    } catch (error) {
      useMattoState.getState().setSnackbarMessage('Failed to copy link');
    }
  };

  const UserAvatar = ({user, size = 32}: {user: TeamsInterface, size?: number}) => {
    const src = getUrlFromRef(user.profile?.files?.pic, true);
    return (
      <Avatar {...{src}} sx={{width: size, height: size, bgcolor: !src ? getAvatarColor(user.email) : undefined}}>
        {user.profile?.first_name?.charAt(0) || user.email.charAt(0)}
      </Avatar>
    );
  };

  const UserInfo = ({user}: {user: TeamsInterface}) => (
    <Box sx={{flexGrow: 1}}>
      <Typography variant="body2">{user.profile?.first_name} {user.profile?.last_name}</Typography>
      <Typography variant="caption" color="text.secondary">{user.email}</Typography>
    </Box>
  );



  const CopyLinkSection = () => {
    // Hide copy link for boards if designerCanMakePublic is not true (only when firm exists)
    if (type === ResourceType.Boards && designFirmID && firm?.designerCanMakePublic !== true) return null;
    return (
      <Box sx={{borderTop: 1, borderColor: 'divider', pt: 1}}>
        <Stack direction="row" alignItems="center" justifyContent={'space-between'} spacing={2}>
          {type !== ResourceType.Boards && (
          <Stack  direction="row" alignItems='center' spacing={2} sx={{cursor:'pointer'}} onClick={handleCopyLink}>
            <Link />            
              <Typography variant="body2" color="primary" sx={{fontWeight: 500}}>            
                Copy { capitalize(resourceName) } link
              </Typography>

          </Stack>
          )}

          {type === ResourceType.Boards && (
            <Box sx={{ width: '100%' }}>
              <Typography variant="h6" component="span" sx={{ mb: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '1.2rem'}}>
                Share Copy of {capitalize(title)}
              </Typography>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ width: '100%', mt: 0.5 }}>
                <Stack  direction="row" alignItems='center' spacing={2} sx={{cursor:'pointer'}} onClick={handleCopyLink}>
                  <Link />            
                    <Typography variant="body2" color="primary" sx={{fontWeight: 500}}>            
                      Copy link
                    </Typography>
                </Stack>

                <FormControlLabel
                  control={<Checkbox checked={preventUserEdit} onChange={e => setPreventUserEdit(e.target.checked)} size="small" />}
                  label={<Typography variant="body2">Prevent users from editing</Typography>}
                  sx={{m: 0}}
                />
              </Stack>
            </Box>
          )}
        </Stack>
      </Box>
    );
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
        <Typography variant="h6" component="span" sx={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
          {title ? `Invite to ${title}` : 'Invite to Collaborate'}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="close"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{pt: 2, height: '340px', display: 'flex', flexDirection: 'column'}}>
        {designFirmID ? (
          <>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 3}}>
              <Autocomplete
                multiple
                sx={{flexGrow: 1}}
                options={teamMemberWithoutAccess}
                getOptionLabel={o => `${o.profile?.first_name || ''} ${o.profile?.last_name || ''}`.trim() || o.email}
                value={validSelectedMembers} 
                onChange={(_, v) => setSelectedMembers(v)}
                onInputChange={(_, v) => setInputValue(v)}
                noOptionsText="No team members available to invite"
                isOptionEqualToValue={(option, value) => option.uid === value.uid}
                renderInput={params => (
                  <TextField {...params}
                    placeholder={validSelectedMembers.length ? "" : "Add Designers By Name or Email"}
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start"><SearchIcon /></InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <UserAvatar user={option} />
                    <UserInfo user={option} />
                  </Box>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip {...getTagProps({index})} key={option.id}
                      label={`${option.profile?.first_name || ''} ${option.profile?.last_name || ''}`.trim() || option.email}
                      size="small"
                      avatar={<UserAvatar user={option} size={24} />}
                    />
                  ))
                }
              />
              {showInviteSection && <AccessSelect value={accessLevel} onChange={e => setAccessLevel(e.target.value)} />}
            </Box>

            {!showInviteSection && (
              <Box sx={{flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0}}>
                { (ownerMember || teamMemberWithAccess.length > 0) ? (
                  <>
                    <Typography variant="subtitle2" sx={{mb: 2, color: 'text.secondary'}}>
                      People with access
                    </Typography>
                    <Box sx={{flex: 1, overflow: 'auto', pr: 1, mb: 2}}>
                      {/* Owner section */}
                      {ownerMember && (
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 2}}>
                          <UserAvatar user={ownerMember} />
                          <UserInfo user={ownerMember} />
                          <Box sx={{minWidth: 125, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                            <Typography variant="body2" color="text.secondary" sx={{fontWeight: 500}}>
                              Owner
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      {/* Team members with access */}
                      {teamMemberWithAccess.map(member => (
                        <Box key={member.id} sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 2}}>
                          <UserAvatar user={member} />
                          <UserInfo user={member} />
                          <AccessSelect value={getCurrentAccessLevel(member)} 
                            onChange={e => handleAccessChange(member, e.target.value)} size="small" 
                            disabled={member.uid ? updatingMembers.has(member.uid) : false}
                          />
                        </Box>
                      ))}
                    </Box>
                  </>
                ) : (
                  <Box sx={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2}}>
                    <Box sx={{p: 2, bgcolor: 'grey.50', borderRadius: 1, textAlign: 'center'}}>
                      <Typography variant="body2" color="text.secondary">
                        No one has access to this {resourceName} yet.
                      </Typography>
                    </Box>
                  </Box>
                )}
                <CopyLinkSection />
              </Box>
            )}

            {showInviteSection && (
              <>
                <TextField fullWidth multiline rows={3} placeholder="Add custom message (optional)" 
                  variant="outlined" sx={{mb: 3}} 
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}/>
                <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 2}}>
                  <ActionButton onClick={onClose} variant="outlined">Cancel</ActionButton>
                  <ActionButton variant="contained" disabled={!validSelectedMembers.length || sendingInvitation} 
                    onClick={handleSendInvitations}>Send invitations</ActionButton>
                </Box>
              </>
            )}
          </>
        ) : (
          <Box sx={{flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3}}>
            {thumbnail && !Array.isArray(thumbnail) && (
              <Box sx={{width: 200, height: 200, borderRadius: 2, overflow: 'hidden', bgcolor: 'grey.100'}}>
                <img src={getUrlFromRef(thumbnail,true)} alt={title || resourceName} style={{width: '100%', height: '100%', objectFit: 'cover'}} />
              </Box>
            )}
            {title && (
              <Typography variant="h6" sx={{textAlign: 'center', color: 'text.primary'}}>
                {title}
              </Typography>
            )}
            <Box sx={{width: '100%'}}><CopyLinkSection /></Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}